/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace
 * @responsibility Define role-scoped navigation metadata for independent desktop and mobile shells.
 * @state-owner none
 * @allowed-imports shared workspace view types
 * @forbidden React; shell components; network; store implementations
 * @public-api workspace navigation metadata
 * @side-effects none
 * @tests frontend/apps/web/e2e/sh-f01.spec.ts
 */

import type { WorkspaceRole, WorkspaceSection } from './model';

export interface WorkspaceNavigationItem {
  readonly k: WorkspaceSection;
  readonly label: string;
  readonly mobileLabelByRole?: Partial<Record<WorkspaceRole, string>>;
  readonly path: string;
}

const NAVIGATION_ITEMS: readonly WorkspaceNavigationItem[] = [
  {
    k: 'act',
    label: '活動',
    // 活動 is the student's home surface, so mobile keeps it labeled 首頁 for that role only.
    mobileLabelByRole: { student: '首頁' },
    path: 'M5 4.5h14v15H5zM8.5 9h7M8.5 13h7M8.5 17h4',
  },
  { k: 'room', label: '班級', path: 'M3 20V8l9-4 9 4v12M9.5 20v-6h5v6' },
  {
    k: 'live',
    label: '即時',
    path: 'M12 4a8 8 0 0 1 8 8M12 20a8 8 0 0 1-8-8M12 9.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5',
  },
  { k: 'rep', label: '報告', path: 'M4 20V4M4 20h16M8.5 16.5V10M12.5 16.5V6.5M16.5 16.5v-4' },
];

const NAVIGATION_ORDER: Readonly<Record<WorkspaceRole, readonly WorkspaceSection[]>> = {
  teacher: ['act', 'room', 'live', 'rep'],
  student: ['room', 'act', 'rep'],
};

export function workspaceNavigationSections(
  role: WorkspaceRole,
): readonly WorkspaceNavigationItem[] {
  return NAVIGATION_ORDER[role].flatMap((key) => NAVIGATION_ITEMS.filter((item) => item.k === key));
}

export function mobileNavigationSections(role: WorkspaceRole): readonly WorkspaceNavigationItem[] {
  // Mobile mirrors the desktop left rail exactly for both roles.
  return workspaceNavigationSections(role).map((item) => ({
    ...item,
    label: item.mobileLabelByRole?.[role] ?? item.label,
  }));
}
